import { createUseTrafficDAG } from "../../../../../libs/traffic-dag/useTrafficDAG";
import TrafficDAG from "../../../../../libs/traffic-dag/TrafficDAG";
import type { Question, Questions } from "../../../utils/qna-tree";
import { PAGE_NAMES } from "@/third-party/mixpanel/page-names";
import { getWindowData, HOME_BIZ_QUESITON_ID } from "./getWindowData";
import { z } from "zod";

//---- SCHEMAS ----
export const staticNodeMetaSchema = z.object({
  // NOTE: put multi line title with \n in the node meta
  title: z.string(),
  pageName: z.string(),
});
export type StaicNodeMeta = z.infer<typeof staticNodeMetaSchema>;

export const questionNodeMetaSchema = z.object({
  title: z.string(),
  pageName: z.string(),
  questionId: z.string(),
  answers: z.array(
    z.object({
      id: z.string(),
      task_id: z.string(),
      text: z.string(),
      nextQuestionId: z.array(z.string()),
    })
  ),
});
export type QuestionNodeMeta = z.infer<typeof questionNodeMetaSchema>;
//---- end of SCHEMAS ----

function getScreenNameByQuestionId(id: number) {
  return id === HOME_BIZ_QUESITON_ID ? PAGE_NAMES.service_quote_flow.is_business_step : PAGE_NAMES.service_quote_flow.qna_step;
}

// ============================
// ======== MAIN EXPORTS ======
// ============================
// Change here the static step names for strong typing
export type StaticGraphNodeId = "root" | "zip" | "additional-info" | "homeowner-full-name" | "homeowner-address" | "homeowner-phone";
export const useSQFGraph = createUseTrafficDAG(buildSQFGraph());
// ============================




/**
 * this is the main function that build the shape of the graph
 * you need to update it if you want to add/remove steps (called node in a graph)
 */
function buildSQFGraph() {
  const { questions_tree, homeowner_info, service_info, sourceId, defaultLocation, defaultZipSource, tcpaText, preferenceTo } = getWindowData();
  // ====== STATIC NODES BEFORE Q&A ======
  // FIXME: when TrafficDAG support Multi Head, we can't remove this root
  const root = new TrafficDAG({
    id: "root" satisfies StaticGraphNodeId,
  });

  const zipNode = root.addChild({
    id: "zip" satisfies StaticGraphNodeId,
    meta: {
      title: ` Find Reliable Local {service_name} for Your Project `,
      pageName: PAGE_NAMES.service_quote_flow.zip_step,
    } satisfies StaicNodeMeta,
  });

  // CHANGE ME HERE if you change the last step before dynamic
  const lastNodeBeforeDynamic = zipNode;
  // ====== DYNMAIC Q&A NODES ======
  // At the root we have two questions that goes one after the other - so we stack them
  const rootQuestions = Object.values(questions_tree);
  if (!(rootQuestions.length >= 1)) {
    throw new Error("UNREACHABLE: questions_tree.length must be at least gte 1.");
  }

  // here we take each question at the root create a graph and stack them one AFTER the other
  // @ts-expect-error
  const businessQuestionNode = stackQuestionsOn(rootQuestions, lastNodeBeforeDynamic);
  if (!businessQuestionNode) {
    throw new Error("UNREACHABLE: we must have a businessQuestionNode at this point");
  }
  if (!(businessQuestionNode.meta?.questionId === String(HOME_BIZ_QUESITON_ID))) {
    throw new Error(`UNREACHABLE: businessQuestionNode has the wrong expected id (${HOME_BIZ_QUESITON_ID}), actual: '${businessQuestionNode.meta?.questionId}'.`);
  }

  // NOTE: the zip step could be skipped over if it's prefilled, in this case
  // we define here a link between the first step to the first question (which is the first child of zip)
  root.linkEdgeById(zipNode.children[0]!.id);


  // ====== STATIC NODES AFTER Q&A =======
  const additionalInfoNode = new TrafficDAG({
    id: "additional-info" satisfies StaticGraphNodeId,
    meta: {
      title: "Add details for more exact quotes",
      pageName: PAGE_NAMES.service_quote_flow.extra_info_step,
    } satisfies StaicNodeMeta,
  })
  // Here businessQuestionNode is still a graph with multiple leaves - so we will need to funnel
  // We funnel to one node
  businessQuestionNode.funnelTo(additionalInfoNode);

  // Here additionalInfoNode is the single leaf of this graph
  const homeownerNameNode = additionalInfoNode.addChild({
    id: "homeowner-full-name" satisfies StaticGraphNodeId,
    meta: {
      title: "Who is the quote for?",
      pageName: PAGE_NAMES.service_quote_flow.full_name_step,
    } satisfies StaicNodeMeta,
  })

  const homeownerAddressNode = homeownerNameNode.addChild({
    id: "homeowner-address" satisfies StaticGraphNodeId,
    meta: {
      title: "Great! Your quote is almost ready",
      pageName: PAGE_NAMES.service_quote_flow.address_step,
    } satisfies StaicNodeMeta,
  })

  homeownerAddressNode.addChild({
    id: "homeowner-phone" satisfies StaticGraphNodeId,
    meta: {
      title: "Last step to request your free quote",
      pageName: PAGE_NAMES.service_quote_flow.phone_step,
    } satisfies StaicNodeMeta,
  });

  // we return the head of the master graph
  return root;
}


//-------------
//--- Utils 
// for Buliding dymanic section
function questionToQuestionId(q: Question) {
  return `q-${q.id}`;
}

function sortQuestions(questions: Questions) {
  return Object.values(questions)
    .sort((a, b) => a.display_order - b.display_order);
}


/**
 * Take each question build a graph and funnel it to the next graph
 * all based on the head
 * head --funnel-> question1graph --funnel-> question2graph --funnel-> ... --funnel-> questionNgraph
 * return the last built graph, could be with one leaf or multiple leaves
 */
function stackQuestionsOn(questions: Questions, head?: TrafficDAG) {
  let last = head;
  sortQuestions(questions)
    .forEach((question) => {
      const qGraph = createQuestionGraph(question);
      if (last) {
        // some question have uneven answers structure, some answers have follow up questions
        // and some don't and need to lead to this last question
        last.forEach(node => {
          // because we change the graph and linked it here
          // we want to stop digging deeper if we found ourself again
          if (node.id === qGraph.id) return;

          const meta = questionNodeMetaSchema.safeParse(node.meta);
          if (meta.success) {
            let shouldBeLinkedToGraph = false;
            (node.meta as QuestionNodeMeta).answers.forEach(ans => {
              if (ans.nextQuestionId.length === 0) {
                ans.nextQuestionId.push(qGraph.id)
                shouldBeLinkedToGraph = true;
              }
            });
            if (node.id !== qGraph.id && shouldBeLinkedToGraph) {
              node.addOrLinkEdge(qGraph);
            }
          }
        })
        // here we take all the leaves of the last graph and point them to the new graph
        last.funnelTo(qGraph);
      }
      last = qGraph;
    });
  return last;
}

function createQuestionGraph(question: Question) {
  const sortedAnswers = Object.values(question.answers)
    .sort((a, b) => a.priority - b.priority);
  const qGraph = new TrafficDAG({
    id: questionToQuestionId(question),
    // Here for those dymanic steps we are adding useful metadata
    // about possible answers
    meta: {
      pageName: getScreenNameByQuestionId(question.id),
      title: question.text,
      questionId: String(question.id),
      answers: sortedAnswers
        .map(ans => ({
          id: String(ans.id),
          task_id: String(ans.task_id),
          text: ans.text,
          // useful for know how to resolve node
          nextQuestionId: Object.values(ans.questions ?? {}).map(q => questionToQuestionId(q)),
        })),
    } satisfies QuestionNodeMeta,
  });
  sortedAnswers.forEach(answer => {
    if (answer.questions) {
      const stacked = stackQuestionsOn(answer.questions);
      if (stacked) {
        qGraph.addEdge(stacked.getHead());
      }
    }
  });
  return qGraph;
}
